import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Checkbox,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  FileUpload as FileUploadIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useAtom } from "jotai";
import Papa from "papaparse";

import { useStaffManagement } from "../../hooks/useStaffManagement";
import { useStaffForm } from "../../hooks/useStaffForm";
import settingsAtom from "../../atoms/settings/SettingsAtom";
import { getInitials, getAvatarColor, themeColors } from "../../utils/content";

// Components
import StaffTable from "./Stafflist/stafftable";
import StaffFormDialog from "./Stafflist/dialogs/staffformdialog";
import { isReadOnly } from "../../utils/permissionUtils";

const StaffList = () => {
  const [settings] = useAtom(settingsAtom);

  // Custom hooks
  const {
    currentStaff,
    orgStaff,
    isLoading,
    activeTab,
    searchQuery,
    selectedStaff,
    setActiveTab,
    setSearchQuery,
    toggleStaffSelection,
    clearSelection,
    loadStaff,
    createStaff,
    updateStaff,
    deleteStaff,
    deallocateStaff,
    importStaff,
  } = useStaffManagement();

  const {
    formData,
    updateField,
    updateDocuments,
    validateForm,
    resetForm,
    setFormDataFromStaff,
    isValid,
  } = useStaffForm();

  // Local state for UI
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load staff on component mount and tab change
  useEffect(() => {
    loadStaff().catch((err) => {
      console.error("Error loading staff:", err);
      showSnackbar("Failed to load staff", "error");
    });
    // eslint-disable-next-line
  }, [loadStaff]);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleTabChange = useCallback(
    (_, newValue) => {
      if (newValue !== null) {
        setActiveTab(newValue);
        setSearchQuery("");
      }
    },
    [setActiveTab, setSearchQuery]
  );

  const handleSearchChange = useCallback(
    (event) => {
      setSearchQuery(event.target.value);
    },
    [setSearchQuery]
  );

  // Dialog handlers
  const openAddDialog = useCallback(() => {
    resetForm();
    setSelectedStaffMember(null);
    setAddDialogOpen(true);
  }, [resetForm]);

  const openEditDialog = useCallback(
    (staffMember) => {
      setSelectedStaffMember(staffMember);
      setFormDataFromStaff(staffMember);
      setEditDialogOpen(true);
    },
    [setFormDataFromStaff]
  );

  const openDeleteDialog = useCallback(
    (staffMember) => {
      setSelectedStaffMember(staffMember);
      setDeleteDialogOpen(true);
    },
    []
  );

  const openImportDialog = useCallback(() => {
    clearSelection();
    setImportDialogOpen(true);
  }, [clearSelection]);

  const closeDialogs = useCallback(() => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setImportDialogOpen(false);
    setSelectedStaffMember(null);
    resetForm();
  }, [resetForm]);

  // CRUD operations
  const handleCreateStaff = useCallback(async () => {
    if (!validateForm()) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      // Always create staff as organization staff first
      await createStaff(formData, true);
      showSnackbar("Staff created successfully");
      closeDialogs();
    } catch (error) {
      console.error("Error creating staff:", error);
      showSnackbar("Failed to create staff", "error");
    }
  }, [validateForm, createStaff, formData, showSnackbar, closeDialogs]);

  const handleUpdateStaff = useCallback(async () => {
    if (!selectedStaffMember || !validateForm()) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      const staffId = selectedStaffMember.id || selectedStaffMember.user?.id;
      await updateStaff(staffId, formData);
      showSnackbar("Staff updated successfully");
      closeDialogs();
    } catch (error) {
      console.error("Error updating staff:", error);
      showSnackbar("Failed to update staff", "error");
    }
  }, [selectedStaffMember, validateForm, updateStaff, formData, showSnackbar, closeDialogs]);

  const handleDeleteStaff = useCallback(async () => {
    if (!selectedStaffMember) return;

    try {
      const staffId = selectedStaffMember.id || selectedStaffMember.user?.id;
      await deleteStaff(staffId, true);
      showSnackbar("Staff deleted successfully");
      closeDialogs();
    } catch (error) {
      console.error("Error deleting staff:", error);
      showSnackbar("Failed to delete staff", "error");
    }
  }, [selectedStaffMember, deleteStaff, showSnackbar, closeDialogs]);

  const handleDeallocateStaff = useCallback(
    async (staffMember) => {
      try {
        const staffId = staffMember.id || staffMember.user?.id;
        await deallocateStaff(staffId, true);
        showSnackbar("Staff deallocated successfully");
      } catch (error) {
        console.error("Error deallocating staff:", error);
        showSnackbar("Failed to deallocate staff", "error");
      }
    },
    [deallocateStaff, showSnackbar]
  );

  const handleImportStaff = useCallback(async () => {
    if (selectedStaff.length === 0) {
      showSnackbar("Please select at least one staff member to import", "warning");
      return;
    }

    try {
      await importStaff(selectedStaff);
      showSnackbar(`Successfully imported ${selectedStaff.length} staff members`);
      closeDialogs();
    } catch (error) {
      console.error("Error importing staff:", error);
      showSnackbar("Failed to import staff", "error");
    }
  }, [selectedStaff, importStaff, showSnackbar, closeDialogs]);

  const handleDirectImport = useCallback(
    async (staffId) => {
      try {
        await importStaff([staffId]);
        showSnackbar("Staff imported successfully");
      } catch (error) {
        console.error("Error importing staff:", error);
        showSnackbar("Failed to import staff", "error");
      }
    },
    [importStaff, showSnackbar]
  );

  // CSV download
  const handleDownloadCSV = useCallback(() => {
    if (!currentStaff || currentStaff.length === 0) {
      showSnackbar("No data to download", "warning");
      return;
    }

    try {
      const csvData = currentStaff.map((staffMember) => ({
        Name: staffMember.user?.name || "-",
        Email: staffMember.user?.email || "-",
        "Mobile Number": `${staffMember.user?.profile_data?.country_code || ""} ${
          staffMember.user?.profile_data?.mobile_number || staffMember.user?.profile_data?.number || ""
        }`.trim() || "-",
        "Job Title": staffMember.user?.job_title || staffMember.user?.profile_data?.role_auth || "-",
        Projects: staffMember.projects?.map((p) => p.project_id).join(", ") || "-",
        "Photo ID": staffMember.user?.profile_data?.photo_id ? "Available" : "Not Available",
        "Other Documents": staffMember.user?.profile_data?.other_documents?.length || 0,
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", `${activeTab === 0 ? "project" : "org"}-staff-list.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      showSnackbar("Failed to download CSV", "error");
    }
  }, [currentStaff, activeTab, showSnackbar]);

  // Form field handlers
  const handleFieldChange = useCallback(
    (field, value) => {
      updateField(field, value);
    },
    [updateField]
  );

  const handleDocumentsChange = useCallback(
    (documents) => {
      updateDocuments(documents);
    },
    [updateDocuments]
  );

  return (
    <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap:2 , justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Staff List
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>  
                <TextField
                  placeholder="Search staff..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{
                    width: 250,
                    transition: "width 0.3s",
                    "& .MuiInputBase-root": { borderRadius: 1 },
                  }}
                  inputProps={{ min: 0, style: { fontSize: '16px', height: "45px" } }}
                />
                 <ToggleButtonGroup
                value={activeTab}
                exclusive
                onChange={handleTabChange}
                aria-label="staff type"
                sx={{
                  border: `1px solid ${themeColors.borderColor}`,
                  ".MuiToggleButton-root": {
                    border: "none",
                    px: 2,
                    textTransform: "capitalize",
                    fontWeight: 500,
                    color: themeColors.textSecondary,
                    "&.Mui-selected": {
                      bgcolor: themeColors.primary,
                      color: themeColors.white,
                      "&:hover": {
                        bgcolor: themeColors.primaryHover,
                      },
                    },
                    "&:hover": {
                      bgcolor: "rgba(241, 98, 83, 0.08)",
                    },
                  },
                }}
              >
                <ToggleButton value={0} aria-label="project staff">
                  Project Staff
                </ToggleButton>
                <ToggleButton value={1} aria-label="org staff">
                  Org Staff
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                disabled ={isReadOnly()}
                sx={{
                  borderColor: themeColors.primary,
                  color: themeColors.primary,
                  "&:hover": {
                    borderColor: themeColors.primaryHover,
                    bgcolor: "rgba(241, 98, 83, 0.08)",
                  },
                  textTransform: "none",
                }}
              >
                Download
              </Button>

              {activeTab === 0 ? (
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: themeColors.primary,
                    "&:hover": { bgcolor: themeColors.primaryHover },
                    textTransform: "none",
                  }}
                  startIcon={<FileUploadIcon />}
                  onClick={openImportDialog}
                  disabled ={isReadOnly()}
                >
                  Import Staff
                </Button>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: themeColors.primary,
                    "&:hover": { bgcolor: themeColors.primaryHover },
                    textTransform: "none",
                  }}
                  startIcon={<AddIcon />}
                  onClick={openAddDialog}
                  disabled ={isReadOnly()}
                >
                  Add Staff
                </Button>
              )}
            </Box>          
        </Box>
      

      <StaffTable
        staff={currentStaff}
        loading={isLoading}
        activeTab={activeTab}
        settings={settings}
        onEdit={openEditDialog}
        onDelete={openDeleteDialog}
        onImport={handleDirectImport}
        onDeallocate={handleDeallocateStaff}
      />

      <StaffFormDialog
        open={addDialogOpen}
        onClose={closeDialogs}
        title="Add Staff Member"
        formData={formData}
        onFieldChange={handleFieldChange}
        onDocumentsChange={handleDocumentsChange}
        onSubmit={handleCreateStaff}
        loading={isLoading}
        isValid={isValid && formData.user.firstname && formData.user.email}
        isEditMode={false}
      />

      {/* Edit Staff Dialog */}
      <StaffFormDialog
        open={editDialogOpen}
        onClose={closeDialogs}
        title="Edit Staff Member"
        formData={formData}
        onFieldChange={handleFieldChange}
        onDocumentsChange={handleDocumentsChange}
        onSubmit={handleUpdateStaff}
        loading={isLoading}
        isValid={isValid && formData.user.firstname && formData.user.email}
        isEditMode={true}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDialogs}>
        <DialogTitle>Delete Staff</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete{" "}
            <strong>{selectedStaffMember?.user?.name || "this staff member"}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStaff}
            variant="contained"
            color="error"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Staff Dialog */}
      <Dialog open={importDialogOpen} onClose={closeDialogs} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>
              Select Staff to Import
            </Typography>
            <IconButton onClick={closeDialogs}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress sx={{ color: themeColors.primary }} />
              </Box>
            ) : orgStaff?.length > 0 ? (
              orgStaff.map((staffMember) => {
                const staffId = staffMember.id || staffMember.user?.id;
                const isSelected = selectedStaff.includes(staffId);

                return (
                  <Box
                    key={staffId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderBottom: "1px solid #eee",
                      bgcolor: isSelected ? "#fff8f8" : "transparent",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      cursor: "pointer",
                    }}
                    onClick={() => toggleStaffSelection(staffId)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStaffSelection(staffId);
                        }}
                        color="primary"
                        sx={{
                          "&.Mui-checked": { color: themeColors.primary },
                        }}
                      />
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          mr: 2,
                          bgcolor: getAvatarColor(staffMember.user?.name),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                        }}
                      >
                        {getInitials(staffMember.user?.name)}
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={isSelected ? "bold" : "normal"}>
                          {staffMember.user?.name || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {staffMember.user?.email || "-"}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={
                        staffMember.user?.job_title ||
                        staffMember.user?.profile_data?.role_auth ||
                        "-"
                      }
                      size="small"
                      sx={{
                        backgroundColor: isSelected ? "#ffebee" : "#E3F2FD",
                        color: isSelected ? "#C62828" : "#1976D2",
                        fontWeight: isSelected ? "bold" : "normal",
                      }}
                    />
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="textSecondary" variant="body1">
                  No staff available to import
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="textSecondary" sx={{ mr: "auto" }}>
            {selectedStaff.length} staff selected
          </Typography>
          <Button onClick={closeDialogs} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleImportStaff}
            disabled={selectedStaff.length === 0 || isLoading}
            variant="contained"
            sx={{
              backgroundColor: themeColors.primary,
              "&:hover": { backgroundColor: themeColors.primaryHover },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Import ${selectedStaff.length} Staff`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffList;